import { reaction } from 'mobx'
import { useObserver } from 'mobx-react'
import { fromPromise } from 'mobx-utils'
import { createContext, useCallback, useContext, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { RootStore, RootType } from './Root'

const StoreContext = createContext<RootType | null>(null)

export function useMobxSelector<T>(selector: () => T): T {
  return useSyncExternalStore(
    // 1. Subscribe function: MobX tells React when the selector output changes
    (onStoreChange) => {
      const dispose = reaction(selector, onStoreChange, { fireImmediately: false })
      return dispose // Cleanup function for React
    },
    // 2. Get Snapshot function: Returns the current value
    selector,
  )
}

function useStores<T>(mapStateToProps: (store: RootType) => T): ReturnType<typeof mapStateToProps> {
  const store = useContext(StoreContext)
  if (store === null) {
    throw new Error('Store cannot be null, please add a context provider')
  }
  return useObserver(() => mapStateToProps(store))
}
const useAPIData = (fn: (...args: any) => Promise<unknown>, ...args: Array<any>) => {
  const argsRef = useRef<any>(args)
  const callAPI = useMemo(() => fromPromise(fn(...argsRef.current)), [fn])

  return useObserver(() => ({
    data: callAPI?.value,
    state: callAPI?.state,
  }))
}

const useLazyApiData = (
  fn: (...args: any) => Promise<unknown>,
  ...args: Array<any>
): [(...args: any[]) => void, { data: any; state: 'pending' | 'fulfilled' | 'rejected' | undefined }] => {
  const [running, setRunning] = useState(0)
  const argsRef = useRef<any>(args)
  const callAPI = useMemo(() => (running > 0 ? fromPromise(fn(...argsRef.current)) : undefined), [fn, running])
  const runFunc = useCallback((...args: any[]) => {
    if (args.length > 0) {
      argsRef.current = [...args]
    }
    setRunning((run) => run + 1)
  }, [])
  return [
    runFunc,
    useObserver(() => ({
      data: callAPI?.value,
      state: callAPI?.state,
    })),
  ]
}

const StoreProvider = StoreContext.Provider

export { RootStore, StoreProvider, useAPIData, useLazyApiData, useStores }
